import { CommentPlan, getCommentType, getCommentOrientation } from '@constants'
import { toNumber, uniq } from 'lodash-es'
import { roofStoreyKey } from '@editorStores'
import { CommentCreateDTO } from '@mutations'

/** Full plan names the comment is shown on. */
export const getCommentPlanLabel = (comment: Comment3D): string => {
  const plans: string[] = []
  if (comment.showInPlans.positionPlan) plans.push('Positionsplan')
  if (comment.showInPlans.stiffeningPlan) plans.push('Aussteifungsplan')
  if (comment.showInPlans.loadPlan) plans.push('Lastplan')
  return plans.join(', ')
}

/** Storey labels for the list, mapping the roof storey key to "D". */
export const getCommentStoreyLabel = (comment: Comment3D): string => {
  const nonRoofStoreys = comment.storeysForPlans.filter(storey => storey !== roofStoreyKey)
  const hasRoof = comment.storeysForPlans.includes(roofStoreyKey)
  const storeyLabels = hasRoof ? [...nonRoofStoreys, 'D'] : nonRoofStoreys
  return storeyLabels.join(', ')
}

/** Builds the create payload for duplicating a comment (offset slightly, prefixed with "Kopie"). */
export const buildDuplicateCommentDTO = (comment: Comment3D): CommentCreateDTO => {
  // Offset in the XY plane (like the in-scene copy) so the copy is visibly moved, not hidden
  // directly behind the original. Callout points shift by the same delta to stay attached.
  const dx = 0.25
  const dy = -0.25

  return {
    position: { x: comment.position.x + dx, y: comment.position.y + dy, z: comment.position.z },
    text: `Kopie ${comment.text}`,
    type: getCommentType(comment),
    orientation: getCommentOrientation(comment),
    color: comment.color,
    backgroundColor: comment.backgroundColor,
    showTitle: comment.showTitle,
    storeysForPlans: comment.storeysForPlans,
    showInPlans: comment.showInPlans,
    callouts:
      comment.callouts?.map(callout => ({
        end: { x: callout.end.x + dx, y: callout.end.y + dy, z: callout.end.z },
        knee: callout.knee
          ? { x: callout.knee.x + dx, y: callout.knee.y + dy, z: callout.knee.z }
          : null,
      })) ?? null,
    storeyForSceneRendering: comment.storeyForSceneRendering || null,
  }
}

/** Case-insensitive match across the comment's text, plan codes and storey labels. */
export const commentMatchesSearch = (comment: Comment3D, search: string): boolean => {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  const haystack = [comment.text, getCommentPlanLabel(comment), getCommentStoreyLabel(comment)]
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

/** Storeys present across the given comments, sorted bottom-to-top with the roof last. */
export const collectCommentStoreys = (comments: Comment3D[]): string[] => {
  const storeys = uniq(comments.flatMap(comment => comment.storeysForPlans ?? []))
  return storeys.sort((a, b) => {
    const aN = a === roofStoreyKey ? Number.MAX_SAFE_INTEGER : toNumber(a)
    const bN = b === roofStoreyKey ? Number.MAX_SAFE_INTEGER : toNumber(b)
    return aN - bN
  })
}

/** True when no plan is selected, or the comment is shown on any of the selected plans. */
export const commentMatchesPlans = (comment: Comment3D, plans: CommentPlan[]): boolean =>
  plans.length === 0 || plans.some(plan => !!comment.showInPlans?.[plan])

/** True when no storey is selected, or the comment targets any of the selected storeys. */
export const commentMatchesStoreys = (comment: Comment3D, storeys: string[]): boolean =>
  storeys.length === 0 || storeys.some(storey => comment.storeysForPlans?.includes(storey))
